import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { authApi } from "../../services/authService";
import { getErrorMessage } from "../../utils/errors";
import { Input } from "../../components/common/Input/Input";
import { Button } from "../../components/common/Button/Button";
import "./Auth.css";

function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const validate = (): boolean => {
    if (!email.trim()) {
      setError(t("auth.validation.emailRequired"));
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t("auth.validation.emailInvalid"));
      return false;
    }
    return true;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");

    if (!validate()) return;

    setLoading(true);
    try {
      await authApi.forgotPassword(email);
      setSent(true);
    } catch (err) {
      setError(
        await getErrorMessage(
          err,
          t("auth.forgotFailed") || "Failed to send reset link",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="auth-page">
      <div className="auth-card">
        <img src="/logo.png" alt="FCS" className="auth-logo" />
        <h1>{t("auth.forgotPassword")}</h1>
        <p className="auth-subtitle">{t("auth.forgotDesc")}</p>

        {error && <p className="auth-error">{error}</p>}

        {sent ? (
          <div className="auth-success">
            <svg
              viewBox="0 0 24 24"
              width="48"
              height="48"
              fill="none"
              stroke="var(--color-success)"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M8 12l2.5 2.5L16 9" />
            </svg>
            <p>{t("auth.resetSent")}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <Input
              id="email"
              type="email"
              label={t("auth.email")}
              placeholder="example@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              floating={true}
              className="form-group"
            />
            <Button type="submit" className="auth-submit-btn" loading={loading}>
              {t("auth.sendReset")}
            </Button>
          </form>
        )}

        <p className="auth-footer">
          <Link to="/login">{t("auth.backToLogin")}</Link>
        </p>
      </div>
    </main>
  );
}

export default ForgotPassword;
